import { useCallback, useEffect, useState } from 'react';
import { api, type Account } from '../lib/api';

export function AccountRadar({ onOpen }: { onOpen: (id: string) => void }) {
  const [rows, setRows] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ tier: '', minProperties: '', minCommercial: '', activeOnly: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (filters.tier) params.tier = filters.tier;
      if (filters.minProperties) params.minProperties = filters.minProperties;
      if (filters.minCommercial) params.minCommercial = filters.minCommercial;
      if (filters.activeOnly) params.hasActiveOpportunities = 'true';
      const res = await api.accounts(params);
      setRows(res.accounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <div className="page-title">ACCOUNT RADAR</div>
      <div className="page-sub">
        Business accounts ranked by potential value — the relationship, not just the job.
      </div>

      <div className="filters">
        <select value={filters.tier} onChange={(e) => setFilters({ ...filters, tier: e.target.value })}>
          <option value="">All tiers</option>
          <option value="CHASE_ACCOUNT">CHASE ACCOUNT</option>
          <option value="HIGH_VALUE">HIGH VALUE</option>
          <option value="WATCH">WATCH</option>
          <option value="JOB_ONLY">JOB ONLY</option>
        </select>
        <select value={filters.minProperties} onChange={(e) => setFilters({ ...filters, minProperties: e.target.value })}>
          <option value="">Any portfolio size</option>
          <option value="5">5+ properties</option>
          <option value="10">10+ properties</option>
          <option value="20">20+ properties</option>
        </select>
        <select value={filters.minCommercial} onChange={(e) => setFilters({ ...filters, minCommercial: e.target.value })}>
          <option value="">Any commercial count</option>
          <option value="3">3+ commercial</option>
          <option value="10">10+ commercial</option>
        </select>
        <label style={{ color: 'var(--text-dim)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={filters.activeOnly}
            onChange={(e) => setFilters({ ...filters, activeOnly: e.target.checked })} />
          Active opportunities only
        </label>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loading && <div className="empty">Loading accounts…</div>}
      {!loading && rows.length === 0 && (
        <div className="empty">
          No accounts yet. Use RESEARCH ACCOUNT on an opportunity to build account intelligence.
        </div>
      )}

      <div className="cards">
        {rows.map((a) => (
          <div className="account-row" key={a.id} onClick={() => onOpen(a.id)}>
            <div className={`score-num ${a.account_score >= 75 ? 'HOT' : a.account_score >= 60 ? 'HIGH' : a.account_score >= 40 ? 'MEDIUM' : 'LOW'}`}
              style={{ fontSize: 30, minWidth: 64, textAlign: 'center' }}>
              {a.account_score}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="account-name">{a.is_demo && '[DEMO] '}{a.display_name}</div>
              <div className="account-stats">
                <span><b>{a.property_count}</b> properties</span>
                <span><b>{a.commercial_count}</b> commercial</span>
                <span><b>{a.active_opportunities}</b> active opportunit{a.active_opportunities === 1 ? 'y' : 'ies'}</span>
                {a.is_verified && <span style={{ color: 'var(--teal)' }}>Business verified</span>}
                {a.phone && <span>{a.phone}</span>}
                {a.website && <span>Website</span>}
              </div>
            </div>
            <span className={`tier ${a.tier}`}>{a.tier.replace('_', ' ')}</span>
            <button className="btn" onClick={(e) => { e.stopPropagation(); onOpen(a.id); }}>OPEN ACCOUNT</button>
          </div>
        ))}
      </div>
    </div>
  );
}
